import { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Trash2, UploadCloud, FileSpreadsheet, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ImportLeads() {
  const { token, user } = useAuth();
  const [leads, setLeads] = useState([{ name: '', email: '', phone: '' }]);
  const [importing, setImporting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const isAdmin = user?.role === 'admin';

  const addLead = () => {
    setLeads([...leads, { name: '', email: '', phone: '' }]);
  };

  const removeLead = (index) => {
    setLeads(leads.filter((_, i) => i !== index));
  };

  const updateLead = (index, field, value) => {
    const newLeads = [...leads];
    newLeads[index][field] = value;
    setLeads(newLeads);
  };

  const handleClearAllLeads = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare TUTTI i lead? Questa azione è irreversibile!')) {
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/admin/clear-all-leads`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.deleted_count} lead cancellati!`);
      setImportResult(null);
    } catch (error) {
      toast.error('Errore nella cancellazione dei lead');
    } finally {
      setDeleting(false);
    }
  };

  const handleImport = async () => {
    const validLeads = leads.filter((lead) => lead.name && lead.email && lead.phone);

    if (validLeads.length === 0) {
      toast.error('Inserisci almeno un lead completo');
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(
        `${API_URL}/leads/import`,
        { leads: validLeads },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.imported} lead importati con successo!`);
      setLeads([{ name: '', email: '', phone: '' }]);
    } catch (error) {
      toast.error('Errore nell\'importazione dei lead');
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Verifica estensione
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Formato file non supportato. Usa .xlsx, .xls o .csv');
      return;
    }

    setUploadingFile(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/leads/import-file`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} nuovi lead importati!`);
      } else {
        toast.info('Nessun nuovo lead da importare (tutti duplicati o non validi)');
      }
    } catch (error) {
      console.error('Errore upload:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione del file');
    } finally {
      setUploadingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Importa Lead</h1>
        <p className="text-slate-600">Carica i lead da file Excel o aggiungili manualmente</p>
      </div>

      {/* Upload File Section */}
      <Card className="bg-white border-2 border-indigo-200 shadow-sm p-6 mb-6" data-testid="file-upload-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-indigo-100 p-3 rounded-lg">
            <FileSpreadsheet className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Importa da File Excel/CSV</h2>
            <p className="text-sm text-slate-600">I duplicati vengono saltati automaticamente (basato sull'email)</p>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-slate-700 mb-2"><strong>Colonne riconosciute:</strong></p>
          <p className="text-sm text-slate-600">
            Nome, Cognome, Email, Telefono + tutti i campi extra del questionario
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFile}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white h-12"
            data-testid="upload-file-button"
          >
            {uploadingFile ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Importazione in corso...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <UploadCloud className="h-5 w-5" />
                <span>Carica File Excel/CSV</span>
              </div>
            )}
          </Button>
        </div>

        {/* Import Result */}
        {importResult && (
          <div className={`mt-4 p-4 rounded-lg ${importResult.imported > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-start gap-3">
              {importResult.imported > 0 ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              )}
              <div>
                <p className={`font-semibold ${importResult.imported > 0 ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {importResult.message}
                </p>
                <div className="mt-2 text-sm">
                  <p className="text-slate-600">
                    <span className="font-medium text-emerald-700">{importResult.imported}</span> nuovi lead importati
                  </p>
                  <p className="text-slate-600">
                    <span className="font-medium text-amber-700">{importResult.skipped}</span> saltati (duplicati o non validi)
                  </p>
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-red-700">Errori:</p>
                    <ul className="text-sm text-red-600 list-disc list-inside">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Manual Entry Section */}
      <Card className="bg-white border border-slate-200 shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Oppure aggiungi manualmente</h2>
        
        <div className="space-y-4" data-testid="import-form">
          {leads.map((lead, index) => (
            <Card key={index} className="border border-slate-200 p-4" data-testid="lead-entry">
              <div className="flex items-start gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Nome *</Label>
                    <Input
                      value={lead.name}
                      onChange={(e) => updateLead(index, 'name', e.target.value)}
                      placeholder="Mario Rossi"
                      className="h-10"
                      data-testid="lead-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Email *</Label>
                    <Input
                      type="email"
                      value={lead.email}
                      onChange={(e) => updateLead(index, 'email', e.target.value)}
                      placeholder="mario@esempio.it"
                      className="h-10"
                      data-testid="lead-email-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Telefono *</Label>
                    <Input
                      type="tel"
                      value={lead.phone}
                      onChange={(e) => updateLead(index, 'phone', e.target.value)}
                      placeholder="+39 123 456 7890"
                      className="h-10"
                      data-testid="lead-phone-input"
                    />
                  </div>
                </div>
                {leads.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLead(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-7"
                    data-testid="remove-lead-button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={addLead}
              className="flex-1"
              data-testid="add-lead-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi Lead
            </Button>
            <Button
              onClick={handleImport}
              disabled={importing}
              className="flex-1 bg-slate-800 hover:bg-slate-900 text-white"
              data-testid="import-button"
            >
              {importing ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Importazione...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <UploadCloud className="h-4 w-4" />
                  <span>Importa {leads.length} Lead</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </Card>

      <div className="bg-slate-100 border border-slate-200 rounded-lg p-6">
        <h3 className="font-semibold text-slate-900 mb-3">Come funziona?</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <span className="text-indigo-600 font-bold">1.</span>
            Scarica il file Excel dal tuo Google Sheet
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-600 font-bold">2.</span>
            Clicca "Carica File Excel/CSV" e seleziona il file
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-600 font-bold">3.</span>
            I lead nuovi vengono importati, i duplicati (stessa email) vengono saltati
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-600 font-bold">4.</span>
            I lead vengono assegnati automaticamente ai commerciali (round-robin)
          </li>
        </ul>
      </div>

      {/* Admin: Clear All Leads */}
      {isAdmin && (
        <Card className="mt-6 bg-red-50 border-2 border-red-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-red-100 p-3 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-900">Zona Pericolosa</h2>
              <p className="text-sm text-red-700">Azioni irreversibili - solo per admin</p>
            </div>
          </div>
          <Button
            onClick={handleClearAllLeads}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="clear-all-leads-button"
          >
            {deleting ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Cancellazione...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                <span>Cancella TUTTI i Lead</span>
              </div>
            )}
          </Button>
        </Card>
      )}
    </div>
  );
}
